// Report-param grammar for the Van Westendorp / Konan block, ported from the coffee block
// (importReportParams* / updateReportParams* / convertOldParamToNew).
//
// The vk<key> section has two grammars. An Xpert report stores an object; every other report
// stores the legacy `!`-joined token string (`p.4!tam.1000!i`). Reading normalizes the string
// into the object form, so there is one reader and two writers.

import { setDefaults } from '@aytm/app/stats/page/question-views/vk/lib/utils'

// Frequency default skips the one-time period (token 0); it is never a valid fq_periods_list option.
export const fqDefault = (availablePeriods = []) => {
  const nonZero = availablePeriods.filter(period => 0 !== period)

  return nonZero.length ? nonZero.at(-1) : availablePeriods.at(-1)
}

export const defaultConfig = answer => {
  if (null == answer || 'loading' === answer) return {}

  return {
    tam: parseFloat(answer.TAM),
    price: parseFloat(answer.clientsPrice),
    increment: !answer.cost_smoothing,
    unit: (answer.costValue || []).map((value, index) => ({
      value: parseInt(value, 10),
      quantity: parseInt(answer.costQuantity[index], 10)
    })),
    update: [],
    delete: []
  }
}

const parseUnit = value => {
  const values = String(value).split('_')

  return values.reduce((unit, _value, index) => {
    if (0 === index % 2) unit.push({ value: parseInt(values[index], 10), quantity: parseInt(values[index + 1], 10) })
    return unit
  }, [])
}

// The legacy token string as the object the reader works with. An object section passes through.
export const convertOldParamToNew = config => {
  if ('string' !== typeof config) return config || {}

  return config.split('!').reduce((acc, token) => {
    const [name, value] = token.split('.')

    if ('p' === name || 'fqp' === name) acc[name] = value
    if ('price' === name || 'tam' === name) acc[name] = parseInt(value, 10)
    if ('unit' === name) acc[name] = parseUnit(value)
    if (['ui', 'i', 'c'].includes(name)) acc[name] = Boolean(Number(value))

    return acc
  }, {})
}

const resolveDefaults = ({ format, answer }) => {
  const { availablePeriods } = setDefaults({ answer })

  return {
    availablePeriods,
    period: undefined !== format?.period ? format.period : availablePeriods.at(-1),
    fqPeriod: undefined !== format?.fq_period ? format.fq_period : fqDefault(availablePeriods)
  }
}

export const resolveReportParams = ({ section, format, components, answer } = {}) => {
  const config = convertOldParamToNew(section)
  const defaults = resolveDefaults({ format, answer })

  const currentConfig = defaultConfig(answer)

  let infographic = Boolean(format && 'infographic' === format?.chartMode)
  if ((!format || 'chart' === format?.chartMode) && Object.hasOwn(config, 'i')) infographic = true
  if (format && 'infographic' === format?.chartMode && Object.hasOwn(config, 'c')) infographic = false

  // a saved number replaces the answer's own; a token that parses to no number keeps the answer's
  if (Number.isFinite(config.price)) currentConfig.price = config.price
  if (Number.isFinite(config.tam)) currentConfig.tam = config.tam
  if (
    Array.isArray(config.unit) &&
    config.unit.every(({ value, quantity }) => Number.isFinite(value) && Number.isFinite(quantity))
  ) {
    currentConfig.unit = [...config.unit]
  }
  if (Object.hasOwn(config, 'ui')) currentConfig.increment = Boolean(Number(config.ui))

  // A stored period is checked against the periods the answer offers, but only once there is an answer:
  // before it arrives nothing says what is offered, and the state seeds again on the first real answer.
  const offered =
    Array.isArray(answer?.available_periods) && answer.available_periods.length ? answer.available_periods : null
  const isOffered = (value, allowOneTime = true) =>
    null === offered || (offered.includes(Number(value)) && (allowOneTime || 0 !== Number(value)))

  return {
    period: Object.hasOwn(config, 'p') && isOffered(config.p) ? config.p : defaults.period,
    // the frequency picker never offers the one-time period
    fqPeriod: Object.hasOwn(config, 'fqp') && isOffered(config.fqp, false) ? config.fqp : defaults.fqPeriod,
    // a question that hides the infographic component cannot open it, whatever the params say
    infographic:
      infographic && (!components || undefined === components.infographic || true === components.infographic),
    config: currentConfig,
    navTo: null,
    loading: false
  }
}

// Which of the block's values differ from what the question declares, in the token vocabulary.
const calcTokens = ({ state, format, answer }) => {
  const defaults = resolveDefaults({ format, answer })
  const { period, fqPeriod, infographic, config = {} } = state
  const update = config.update || []

  const tokens = {}

  if (Number(period) !== Number(defaults.period)) tokens.p = period
  if (Number(fqPeriod) !== Number(defaults.fqPeriod)) tokens.fqp = fqPeriod

  if (update.includes('price')) tokens.price = config.price
  if (update.includes('tam')) tokens.tam = config.tam
  if (update.includes('cost')) tokens.unit = [...config.unit]
  if (update.includes('incr')) tokens.ui = Number(config.increment)

  // the infographic is stored as the deviation from whichever view the question opens in
  if ((!format || !format?.chartMode || 'chart' === format.chartMode) && infographic) tokens.i = true
  if (format && 'infographic' === format?.chartMode && !infographic) tokens.c = true

  return tokens
}

// Xpert grammar: the stored object, updated in place so unknown keys survive.
export const calcReportParamsSection = ({ state, section, format, answer } = {}) => {
  const config = convertOldParamToNew(section)
  const tokens = calcTokens({ state, format, answer })

  for (const name of ['p', 'fqp', 'i', 'c']) {
    if (Object.hasOwn(tokens, name)) config[name] = tokens[name]
    else delete config[name]
  }
  for (const name of ['price', 'tam', 'unit', 'ui']) {
    if (Object.hasOwn(tokens, name)) config[name] = tokens[name]
  }

  return Object.keys(config).length ? config : null
}

// Legacy grammar: the block's own tokens first, then whatever else the section carried.
export const calcReportParamsSectionLegacy = ({ state, section, format, answer } = {}) => {
  const tokens = calcTokens({ state, format, answer })

  const written = []
  const owned = ['p', 'fqp', 'price', 'tam', 'unit', 'ui']

  if (Object.hasOwn(tokens, 'p')) written.push(`p.${tokens.p}`)
  if (Object.hasOwn(tokens, 'fqp')) written.push(`fqp.${tokens.fqp}`)
  if (Object.hasOwn(tokens, 'price')) written.push(`price.${tokens.price}`)
  if (Object.hasOwn(tokens, 'tam')) written.push(`tam.${tokens.tam}`)
  if (Object.hasOwn(tokens, 'unit')) {
    written.push(`unit.${tokens.unit.map(item => `${item.value}_${item.quantity}`).join('_')}`)
  }
  if (Object.hasOwn(tokens, 'ui')) written.push(`ui.${tokens.ui}`)
  if (Object.hasOwn(tokens, 'i')) written.push('i')
  if (Object.hasOwn(tokens, 'c')) written.push('c')

  const kept = (Array.isArray(section) ? section : String(section || '').split('!'))
    .filter(token => token)
    .filter(token => {
      const name = token.split('.')[0]

      return !['update', 'i', 'c'].includes(name) && !(owned.includes(name) && Object.hasOwn(tokens, name))
    })

  const ret = [...written, ...kept]

  return ret.length ? ret.join('!') : null
}
